import React from 'react';
import _isEmpty from 'lodash/isEmpty';

import { If } from 'utils';
import {
  Loading,
  PageErrorUi,
  PriceInput,
  StickyButtons,
  UiKit
} from 'components';
import NoOptions from './NoOptions';
import { useStoreCurrency, useSupportedProductAttributes } from 'utils/hooks';
import { useProductsCatalog } from '../../hooks';
import { getCopies } from '../../methods';

export default function Config() {
  const storeCurrency = useStoreCurrency();
  const {
    supportsType,
    supportsCategory,
    supportsCollection
  } = useSupportedProductAttributes();

  const {
    isError,
    isLoading,
    entity,
    data,
    initialData,
    errors,
    isDirty,
    productTypeOptions,
    productCategoryOptions,
    collectionOptions,
    isProcessing,
    onChange,
    onReload,
    onReset,
    onSave
  } = useProductsCatalog();

  const copies = getCopies(entity, supportsType, supportsCategory);

  return (
    <PageErrorUi isError={isError} onReload={onReload}>
      <Loading loading={isLoading}>
        <UiKit.Grid templateColumns="1fr" gap="36px">
          <UiKit.Grid templateColumns="1fr 1fr" gap="36px">
            <If test={supportsType}>
              <UiKit.Select
                isMulti
                menuIsOpen
                menuPosition="relative"
                label={copies.productTypes.label}
                caption={copies.productTypes.description}
                options={productTypeOptions}
                value={data.productTypes}
                onChange={data => onChange({ productTypes: data })}
                placeholder="Search..."
                isDisabled={_isEmpty(productTypeOptions) || isProcessing}
                noOptionsMessage={() => (
                  <NoOptions>There are no product types.</NoOptions>
                )}
                error={errors.productTypes}
              />
            </If>
            <If test={supportsCategory}>
              <UiKit.Select
                isMulti
                isNested
                menuIsOpen
                menuPosition="relative"
                label={copies.productCategories.label}
                caption={copies.productCategories.description}
                options={productCategoryOptions}
                value={data.productCategories}
                onChange={value => onChange({ productCategories: value })}
                placeholder="Search..."
                isDisabled={_isEmpty(productCategoryOptions) || isProcessing}
                noOptionsMessage={() => (
                  <NoOptions>There are no product categories.</NoOptions>
                )}
                error={errors.productCategories}
              />
            </If>
            <If test={supportsCollection}>
              <UiKit.Select
                isMulti
                menuIsOpen
                menuPosition="relative"
                label={copies.collections.label}
                caption={copies.collections.description}
                options={collectionOptions}
                value={data.collections}
                onChange={data => onChange({ collections: data })}
                placeholder="Search..."
                isDisabled={_isEmpty(collectionOptions) || isProcessing}
                noOptionsMessage={() => (
                  <NoOptions>There are no collections.</NoOptions>
                )}
                error={errors.collections}
              />
            </If>
          </UiKit.Grid>
          <UiKit.Grid templateColumns="1fr 1fr" gap="36px">
            <UiKit.Select
              isMulti
              isCreatable
              hideSelectAllOption
              label={copies.tags.label}
              caption={copies.tags.description}
              value={data.excludedTags}
              options={data.excludedTags}
              onChange={data => onChange({ excludedTags: data })}
              isDisabled={isProcessing}
              placeholder="Enter the product tags here..."
              noOptionsMessage={() => 'Type the product tag'}
              formatCreateLabel={input => `Add tag, "${input}"`}
              error={errors.excludedTags}
            />
            <PriceInput
              label={copies.price.label}
              caption={copies.price.description}
              currency={storeCurrency}
              value={data.minPrice}
              onChange={value => onChange({ minPrice: value })}
              disabled={isProcessing}
              error={errors.minPrice}
            />
            <UiKit.Toggle
              size="sm"
              direction="rtl"
              label={copies.digitalProducts.label}
              caption={copies.digitalProducts.description}
              isActive={data.showDigitalProducts}
              onChange={value => onChange({ showDigitalProducts: value })}
              disabled={isProcessing}
              error={errors.showDigitalProducts}
            />
          </UiKit.Grid>
        </UiKit.Grid>
        <StickyButtons
          alwaysEdit
          data={data}
          initialData={initialData}
          onConfirm={() => onSave()}
          isProcessing={isProcessing}
        >
          <UiKit.Button
            size="lg"
            variant="ghost"
            onClick={() => onReset()}
            disabled={isProcessing || !isDirty}
          >
            Reset
          </UiKit.Button>
          <UiKit.Button
            size="lg"
            onClick={() => onSave()}
            processing={isProcessing}
            disabled={!isDirty}
          >
            Save Changes
          </UiKit.Button>
        </StickyButtons>
      </Loading>
    </PageErrorUi>
  );
}
